import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { translate } from '@jsverse/transloco';
import {
    NgxExtendedPdfViewerModule,
    NgxExtendedPdfViewerService,
    PagesLoadedEvent,
    PasswordPrompt,
    pdfDefaultOptions,
    PdfLoadedEvent,
    PdfSidebarView,
    SpreadType,
    ZoomType,
} from 'ngx-extended-pdf-viewer';
import { ButtonComponent, DialogComponent, InputComponent, ProgressBarComponent } from 'vcp-common-ui-lib';
import { PdfViewerPasswordPromptService } from './pdf-viewer-password-prompt.service';

interface PdfPrintProgressEvent {
    type: string;
    percent: number;
}

@Component({
    selector: 'vc-pdf-viewer',
    imports: [
        CommonModule,
        NgxExtendedPdfViewerModule,
        ButtonComponent,
        DialogComponent,
        InputComponent,
        ProgressBarComponent,
        FormsModule,
        MatFormField,
        MatSelect,
        MatOption,
    ],
    providers: [NgxExtendedPdfViewerService, PdfViewerPasswordPromptService],
    templateUrl: './pdf-viewer.component.html',
    styleUrl: './pdf-viewer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class PdfViewerComponent implements AfterViewInit, OnDestroy {
    private static _pagesPerViewLabelIdCounter = 0;
    readonly pdfSidebarView = PdfSidebarView;

    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly _pdfViewerService: NgxExtendedPdfViewerService = inject(NgxExtendedPdfViewerService);
    readonly passwordPrompt: PdfViewerPasswordPromptService = inject(PdfViewerPasswordPromptService);

    private readonly _previousPasswordPrompt: PasswordPrompt | null | undefined = pdfDefaultOptions.passwordPrompt;

    readonly firstPageLabel = translate('PDF_VIEWER.PAGINATION.FIRST_PAGE');
    readonly previousPageLabel = translate('PDF_VIEWER.PAGINATION.PREVIOUS_PAGE');
    readonly nextPageLabel = translate('PDF_VIEWER.PAGINATION.NEXT_PAGE');
    readonly lastPageLabel = translate('PDF_VIEWER.PAGINATION.LAST_PAGE');
    readonly zoomInLabel = translate('PDF_VIEWER.ZOOM.ZOOM_IN');
    readonly zoomOutLabel = translate('PDF_VIEWER.ZOOM.ZOOM_OUT');
    readonly fitToWidthLabel = translate('PDF_VIEWER.ZOOM.FIT_TO_WIDTH');
    readonly fitToPageLabel = translate('PDF_VIEWER.ZOOM.FIT_TO_PAGE');
    readonly printLabel = translate('PDF_VIEWER.ACTION.PRINT');
    readonly downloadLabel = translate('COMMON_UI.DOWNLOAD');
    readonly thumbnailsLabel = translate('PDF_VIEWER.SIDEBAR.THUMBNAILS');
    readonly outlineLabel = translate('PDF_VIEWER.SIDEBAR.OUTLINE');
    readonly printProgressTitle = translate('PDF_VIEWER.PRINT.TITLE');
    readonly printProgressMessage = translate('PDF_VIEWER.PRINT.PREPARING');
    readonly printCancelLabel = translate('COMMON_UI.CANCEL');
    readonly passwordDialogTitle = translate('COMMON_UI.INPUT.PASSWORD');
    readonly passwordDialogMessage = translate('PDF_VIEWER.PASSWORD.MESSAGE');
    readonly passwordInputLabel = translate('COMMON_UI.INPUT.PASSWORD');
    readonly passwordOkLabel = translate('PDF_VIEWER.PASSWORD.OK');
    readonly passwordCancelLabel = translate('COMMON_UI.CANCEL');
    readonly passwordInvalidMessage = translate('PDF_VIEWER.PASSWORD.INVALID');
    readonly pagesPerViewLabel = translate('PDF_VIEWER.PAGES_PER_VIEW.LABEL');
    readonly pagesPerViewLabelId = `vc-pdf-viewer-pages-per-view-label-${++PdfViewerComponent._pagesPerViewLabelIdCounter}`;

    readonly minZoomPercent = 10;
    readonly maxZoomPercent = 400;
    readonly pagesPerViewOptions: number[] = [1, 2];
    private readonly _zoomStepFactor = 1.1;
    private _disableBuiltInZoomPercentL10nTimeout?: NodeJS.Timeout;

    currentPage: number = 1;
    totalPages: number = 0;
    currentZoom: ZoomType = 'auto';
    currentZoomScale: number = 1;
    hasOutline: boolean = false;
    activeSidebarView: PdfSidebarView = PdfSidebarView.THUMBS;
    sidebarPanelVisible: boolean = true;
    showPrintProgressDialog: boolean = false;
    printProgressPercent: number = 0;
    pagesPerView: number = 1;

    @Input()
    src!: string | ArrayBuffer | Blob | Uint8Array | URL | undefined;

    @Input()
    filenameForDownload: string | undefined;

    @Output()
    pageChange = new EventEmitter<number>();

    @Output()
    pdfLoaded = new EventEmitter<PdfLoadedEvent>();

    @Output()
    pdfLoadingFailed = new EventEmitter<Error>();

    @Output()
    passwordPromptCancelled = new EventEmitter<void>();

    @ViewChild('passwordInput')
    passwordInput?: InputComponent;

    constructor() {
        pdfDefaultOptions.passwordPrompt = this.passwordPrompt;
    }

    ngAfterViewInit(): void {
        if (this.passwordInput) {
            this.passwordPrompt.registerPasswordInput(this.passwordInput, this.passwordInvalidMessage);
        }
    }

    get isFirstPageDisabled(): boolean {
        return this.totalPages === 0 || this.currentPage <= 1;
    }

    get isLastPageDisabled(): boolean {
        return this.totalPages === 0 || this.currentPage >= this.totalPages;
    }

    get isZoomOutDisabled(): boolean {
        return this.currentZoomScale <= this.minZoomPercent / 100;
    }

    get isZoomInDisabled(): boolean {
        return this.currentZoomScale >= this.maxZoomPercent / 100;
    }

    get isFitZoomDisabled(): boolean {
        return this.totalPages === 0;
    }

    get isFileActionDisabled(): boolean {
        return this.totalPages === 0;
    }

    get isSidebarActionDisabled(): boolean {
        return this.totalPages === 0;
    }

    get showThumbnailButton(): boolean {
        return this.totalPages > 1;
    }

    get showOutlineButton(): boolean {
        return this.hasOutline;
    }

    get sidebarVisible(): boolean {
        return this.sidebarPanelVisible && (this.showThumbnailButton || this.showOutlineButton);
    }

    get spread(): SpreadType {
        return this.pagesPerView === 2 ? 'odd' : 'off';
    }

    get isPagesPerViewDisabled(): boolean {
        return this.totalPages === 0;
    }

    get thumbnailButtonMode(): 'icon-tertiary' | 'icon-plain' {
        return this.activeSidebarView === PdfSidebarView.THUMBS && this.sidebarPanelVisible
            ? 'icon-tertiary'
            : 'icon-plain';
    }

    get outlineButtonMode(): 'icon-tertiary' | 'icon-plain' {
        return this.activeSidebarView === PdfSidebarView.OUTLINE && this.sidebarPanelVisible
            ? 'icon-tertiary'
            : 'icon-plain';
    }

    get pageLabel(): string {
        if (this.totalPages === 0) {
            return '';
        }

        return translate('PDF_VIEWER.PAGINATION.PAGE_OF', {
            PH: this.currentPage,
            PH_1: this.totalPages,
        });
    }

    get zoomLabel(): string {
        if (this.totalPages === 0) {
            return '';
        }

        return `${Math.round(this.currentZoomScale * 100)}%`;
    }

    goToFirstPage(): void {
        this.setPage(1);
    }

    goToPreviousPage(): void {
        this.setPage(this.currentPage - 1);
    }

    goToNextPage(): void {
        this.setPage(this.currentPage + 1);
    }

    goToLastPage(): void {
        this.setPage(this.totalPages);
    }

    zoomIn(): void {
        this.applyZoom(this.currentZoomScale * this._zoomStepFactor);
    }

    zoomOut(): void {
        this.applyZoom(this.currentZoomScale / this._zoomStepFactor);
    }

    fitToWidth(): void {
        this.currentZoom = 'page-width';
        this._changeDetectorRef.markForCheck();
    }

    fitToPage(): void {
        this.currentZoom = 'page-fit';
        this._changeDetectorRef.markForCheck();
    }

    showThumbnails(): void {
        if (this.sidebarPanelVisible && this.activeSidebarView === PdfSidebarView.THUMBS) {
            this.hideSidebar();
            return;
        }

        this.openSidebar(PdfSidebarView.THUMBS);
    }

    showOutline(): void {
        if (this.sidebarVisible && this.activeSidebarView === PdfSidebarView.OUTLINE) {
            this.hideSidebar();
            return;
        }

        this.openSidebar(PdfSidebarView.OUTLINE);
    }

    printFile(): void {
        if (this.isFileActionDisabled) {
            return;
        }

        this._pdfViewerService.print();
    }

    async downloadFile(): Promise<void> {
        if (this.isFileActionDisabled) {
            return;
        }

        const blob = await this._pdfViewerService.getCurrentDocumentAsBlob();

        if (!blob) {
            return;
        }

        const filename = this.getDownloadFilename();

        if (await this.saveBlobWithFilePicker(blob, filename)) {
            return;
        }

        this.downloadBlobWithAnchor(blob, filename);
    }

    onCurrentZoomFactor(scale: number): void {
        if (!Number.isFinite(scale)) {
            return;
        }

        this.currentZoomScale = scale;
        this._changeDetectorRef.markForCheck();
    }

    onPdfLoaded(event: PdfLoadedEvent): void {
        this.passwordPrompt.close();
        this.currentPage = 1;
        this.totalPages = 0;
        this.currentZoom = 'auto';
        this.currentZoomScale = 1;
        this.hasOutline = false;
        this.activeSidebarView = PdfSidebarView.THUMBS;
        this.sidebarPanelVisible = true;
        this.pagesPerView = 1;
        this.pdfLoaded.emit(event);
        this._changeDetectorRef.markForCheck();
        this.scheduleDisableBuiltInZoomPercentL10n();
    }

    onPagesLoaded(event: PagesLoadedEvent): void {
        this.totalPages = event.pagesCount;
        this._changeDetectorRef.markForCheck();
        this.scheduleDisableBuiltInZoomPercentL10n();
    }

    onViewerPageChange(page: number | undefined): void {
        if (page === undefined) {
            return;
        }
        if (page !== this.currentPage) {
            this.currentPage = page;
            this.pageChange.emit(page);
            this._changeDetectorRef.markForCheck();
        }
    }

    onPdfLoadingFailed(error: Error): void {
        this.pdfLoadingFailed.emit(error);
    }

    onOutlineLoaded(event: { outlineCount: number }): void {
        this.hasOutline = event.outlineCount > 0;
        this._changeDetectorRef.markForCheck();
    }

    onActiveSidebarViewChange(view: PdfSidebarView): void {
        if (view > PdfSidebarView.NONE) {
            this.activeSidebarView = view;
            this.sidebarPanelVisible = true;
            this._changeDetectorRef.markForCheck();
            return;
        }

        if (view === PdfSidebarView.NONE || view === PdfSidebarView.UNKNOWN) {
            this.sidebarPanelVisible = false;
            this._changeDetectorRef.markForCheck();
        }
    }

    onBeforePrint(): void {
        this.printProgressPercent = 0;
        this.showPrintProgressDialog = true;
        this._changeDetectorRef.markForCheck();
    }

    onAfterPrint(): void {
        this.closePrintProgressDialog();
    }

    onPrintProgress(event: PdfPrintProgressEvent): void {
        if (event.type !== 'print') {
            return;
        }

        this.printProgressPercent = Math.round(Math.min(100, Math.max(0, event.percent)));
        this._changeDetectorRef.markForCheck();
    }

    cancelPrint(): void {
        const cancelButton = this._elementRef.nativeElement.querySelector('#printCancel') as HTMLButtonElement | null;
        cancelButton?.click();
        this.closePrintProgressDialog();
    }

    cancelPasswordPrompt(): void {
        this.passwordPrompt.cancel();
        this.passwordPromptCancelled.emit();
    }

    onPagesPerViewChange(): void {
        this._changeDetectorRef.markForCheck();
    }

    private closePrintProgressDialog(): void {
        this.showPrintProgressDialog = false;
        this.printProgressPercent = 0;
        this._changeDetectorRef.markForCheck();
    }

    private openSidebar(view: PdfSidebarView): void {
        this.sidebarPanelVisible = true;

        if (this.activeSidebarView === view) {
            this._changeDetectorRef.markForCheck();
            return;
        }

        this.activeSidebarView = view;
        this._changeDetectorRef.markForCheck();
    }

    private hideSidebar(): void {
        this.sidebarPanelVisible = false;
        this._changeDetectorRef.markForCheck();
    }

    private setPage(page: number): void {
        if (page < 1 || (this.totalPages > 0 && page > this.totalPages) || page === this.currentPage) {
            return;
        }

        this.currentPage = page;
        this.pageChange.emit(page);
        this._changeDetectorRef.markForCheck();
    }

    private applyZoom(scale: number): void {
        const minScale = this.minZoomPercent / 100;
        const maxScale = this.maxZoomPercent / 100;
        const clampedScale = Math.min(maxScale, Math.max(minScale, scale));

        this.currentZoom = `${Math.round(clampedScale * 100)}%`;
        this._changeDetectorRef.markForCheck();
    }

    private scheduleDisableBuiltInZoomPercentL10n(): void {
        clearTimeout(this._disableBuiltInZoomPercentL10nTimeout);
        this._disableBuiltInZoomPercentL10nTimeout = setTimeout(() => {
            this._disableBuiltInZoomPercentL10nTimeout = undefined;
            this.disableBuiltInZoomPercentL10n();
        });
    }

    private disableBuiltInZoomPercentL10n(): void {
        this._elementRef.nativeElement
            .querySelectorAll('[data-l10n-id="pdfjs-page-scale-percent"]')
            .forEach((element) => {
                element.removeAttribute('data-l10n-id');
                element.removeAttribute('data-l10n-args');
            });
    }

    private getDownloadFilename(): string {
        const trimmedFilename = this.filenameForDownload?.trim();
        const filename = trimmedFilename?.length ? trimmedFilename : 'document.pdf';

        return filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
    }

    private async saveBlobWithFilePicker(blob: Blob, filename: string): Promise<boolean> {
        const showSaveFilePicker = (
            window as Window & {
                showSaveFilePicker?: (options: {
                    suggestedName?: string;
                    types?: Array<{ description: string; accept: Record<string, string[]> }>;
                }) => Promise<FileSystemFileHandle>;
            }
        ).showSaveFilePicker;

        if (!showSaveFilePicker) {
            return false;
        }

        try {
            const handle = await showSaveFilePicker({
                suggestedName: filename,
                types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return true;
        } catch (error) {
            return error instanceof DOMException && error.name === 'AbortError';
        }
    }

    private downloadBlobWithAnchor(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    ngOnDestroy(): void {
        clearTimeout(this._disableBuiltInZoomPercentL10nTimeout);

        // Restore the default password prompt
        if (pdfDefaultOptions.passwordPrompt === this.passwordPrompt) {
            pdfDefaultOptions.passwordPrompt = this._previousPasswordPrompt;
        }
    }
}
